import React from 'react';
import { Input, InputNumber } from 'antd';
import { F913 } from '../../../services/pw9213Api';

interface MasterInfoProps {
  formData: Partial<F913>;
  names: Record<string, string>;
  isEditing: boolean;
  getField: <K extends keyof F913>(key: K) => F913[K] | string;
  setField: <K extends keyof F913>(key: K, value: F913[K]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  getFieldError?: (field: string) => string | undefined;
  mode: 'browse' | 'add' | 'edit';
}

const FieldError: React.FC<{ error?: string }> = ({ error }) =>
  error ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6, fontWeight: 'normal' }}>{error}</span> : null;

const MasterInfo: React.FC<MasterInfoProps> = ({
  formData, names, isEditing,
  getField, setField, resolveSingleName, getFieldError, mode,
}) => {
  const E = (field: string) => <FieldError error={getFieldError?.(field)} />;

  const isAddMode = mode === 'add';

  return (
    <div className="pw9213-master-info">
      {/* Header row: Employee Code, Department, Year */}
      <div className="pw9213-header-row">
        <div className="pw9213-field-group">
          <label className="required">員工代號{E('f913_01')}</label>
          <div className="pw9213-lookup-field">
            <Input
              style={{ width: 120 }}
              maxLength={8}
              disabled={!isAddMode}
              value={getField('f913_01') as string}
              onChange={e => setField('f913_01', e.target.value)}
              onBlur={() => resolveSingleName('employee', 'employee', getField('f913_01') as string)}
              placeholder="員工代號"
            />
            <span className="pw9213-lookup-name">{names.employee || '--'}</span>
          </div>
        </div>

        <div className="pw9213-field-group">
          <label>部門</label>
          <Input
            style={{ width: 140 }}
            readOnly
            value={names.department || ''}
            placeholder="（自動帶入）"
          />
        </div>

        <div className="pw9213-field-group">
          <label className="required">年度{E('f913_02')}</label>
          <Input
            style={{ width: 80 }}
            maxLength={4}
            disabled={!isAddMode}
            value={getField('f913_02') as string}
            onChange={e => setField('f913_02', e.target.value)}
            placeholder="YYYY"
          />
        </div>
      </div>

      {/* Master fields: Annual leave data */}
      <div className="pw9213-master-fields">
        <div className="pw9213-field-section">
          <span className="section-title">特休今年新增</span>
          <div className="pw9213-field-pair">
            <div className="pw9213-field-group">
              <label>天數{E('f913_11')}</label>
              <InputNumber
                style={{ width: 100 }}
                min={0}
                precision={2}
                disabled={!isEditing}
                value={formData.f913_11 ?? 0}
                onChange={v => setField('f913_11', v ?? 0)}
              />
            </div>
            <div className="pw9213-field-group">
              <label>天數(備用)</label>
              <InputNumber
                style={{ width: 100 }}
                min={0}
                precision={2}
                disabled={!isEditing}
                value={formData.f913_111 ?? 0}
                onChange={v => setField('f913_111', v ?? 0)}
              />
            </div>
          </div>
        </div>

        <div className="pw9213-field-section">
          <span className="section-title">特休去年剩餘</span>
          <div className="pw9213-field-pair">
            <div className="pw9213-field-group">
              <label>天數{E('f913_12')}</label>
              <InputNumber
                style={{ width: 100 }}
                min={0}
                precision={2}
                disabled={!isEditing}
                value={formData.f913_12 ?? 0}
                onChange={v => setField('f913_12', v ?? 0)}
              />
            </div>
            <div className="pw9213-field-group">
              <label>天數(備用)</label>
              <InputNumber
                style={{ width: 100 }}
                min={0}
                precision={2}
                disabled={!isEditing}
                value={formData.f913_121 ?? 0}
                onChange={v => setField('f913_121', v ?? 0)}
              />
            </div>
          </div>
        </div>

        <div className="pw9213-field-section">
          <span className="section-title">特休期初已用</span>
          <div className="pw9213-field-pair">
            <div className="pw9213-field-group">
              <label>天數{E('f913_21')}</label>
              <InputNumber
                style={{ width: 100 }}
                min={0}
                precision={2}
                disabled={!isEditing}
                value={formData.f913_21 ?? 0}
                onChange={v => setField('f913_21', v ?? 0)}
              />
            </div>
            <div className="pw9213-field-group">
              <label>天數(備用)</label>
              <InputNumber
                style={{ width: 100 }}
                min={0}
                precision={2}
                disabled={!isEditing}
                value={formData.f913_211 ?? 0}
                onChange={v => setField('f913_211', v ?? 0)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(MasterInfo);
